import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Percent, BadgeDollarSign, Copy, Gift, Tag, Baby, Package, Ticket, PenLine,
} from "lucide-react";

/**
 * The nine offer templates from spec 9.1. Field sets mirror the payload models in
 * backend/models/offers.py, so what the form can produce is exactly what the API
 * accepts.
 *
 * Only Percentage Discount and Dollar Discount have field lists in the spec; the other
 * seven are named without one and carry a minimal shape until specified.
 */

// One icon per template (round 9, TP-6): "the offer templates should all have some
// type of small icon/render for each one kind of like UberEats". Lucide, which the app
// already ships, so there is no new dependency and nothing to host. Each is chosen to
// be recognisable at 16px rather than to be clever - a Percent for a percentage, a
// Baby for Kids Eat Free.
export const OFFER_TEMPLATES = [
  { value: "PERCENTAGE_DISCOUNT", label: "Percentage Discount", hint: "e.g. 20% off the total bill", icon: Percent },
  { value: "DOLLAR_DISCOUNT", label: "Dollar Discount", hint: "e.g. $10 off any main", icon: BadgeDollarSign },
  { value: "BOGO", label: "Buy One Get One", hint: "Buy one item, get one free", icon: Copy },
  { value: "FREE_ITEM", label: "Free Item", hint: "A free item with any purchase", icon: Gift },
  { value: "FIXED_PRICE", label: "Fixed Price", hint: "A set price for a named item", icon: Tag },
  { value: "KIDS_EAT_FREE", label: "Kids Eat Free", hint: "Children eat free with an adult meal", icon: Baby },
  { value: "PACKAGE_DEAL", label: "Package Deal", hint: "Several items bundled together", icon: Package },
  { value: "EXPERIENCE", label: "Experience", hint: "Something to do, not just to buy", icon: Ticket },
  { value: "CUSTOM", label: "Custom Offer", hint: "Write your own, fully editable", icon: PenLine },
];

// spec 9.1: "a selector in 5% increments from 10% to 95%". Rendered as a Select rather
// than a slider so the allowed set is visible and nothing else is reachable - the
// backend rejects non-multiples of 5, and the UI should make that impossible rather
// than merely handled.
export const DISCOUNT_STEPS = Array.from({ length: 18 }, (_, i) => 10 + i * 5);

// Round 4, G-10: "should work same as % going up by increments of $5 and user just
// clicks which one". $5 to $100.
//
// The same trap as the percentage steps, and worth naming because it is not obvious:
// Smart Offer bounds validate against THIS list, so an amount the selector offers but
// the bounds reject produces a form that cannot be saved. One constant, both uses.
//
// Below $5 is not a discount worth a card; $100 is high enough for a package without
// making the list unusable.
export const DOLLAR_STEPS = Array.from({ length: 20 }, (_, i) => 5 + i * 5);

// Round 7, SP-25: "lets $ go after number should force it in front".
//
// This landed as a blocking validation first and Robin revised it: "i think it can just
// auto-correct it instead of showing the warning - easier and less friction for user?"
// He is right. Refusing a save over punctuation stops a merchant who has finished
// writing, to teach them a convention they do not need to know.
//
// So it is corrected on blur, with a note saying what changed - a note rather than a
// warning, because silently rewriting somebody's own words is the part worth avoiding.
// The backend still normalises (campaign_rules.normalise_currency) as the backstop for
// API clients, which are not bound by this form.
const TRAILING_DOLLAR = /(?<![\w$])(\d[\d,]*(?:\.\d{1,2})?)\s*\$/;

/** The first badly written amount in some text, or null. */
export function badCurrency(text) {
  const m = TRAILING_DOLLAR.exec(String(text || ""));
  return m ? m[0].trim() : null;
}

/** Move every trailing dollar sign to the front. Returns the text unchanged if clean. */
export function fixCurrency(text) {
  const str = String(text ?? "");
  if (!str.includes("$")) return str;
  return str.replace(new RegExp(TRAILING_DOLLAR, "g"), (_, amount) => "$" + amount);
}

// Round 7, SP-24. An Experience says what the customer GETS, not what it costs:
//
//   "Experience still does not really tell me what the offer is eg: user type in tree
//    top adventure it only shows up as tree top adventure."
//
// These three are deliberately the mechanics other templates already have. Joel asked
// for that: an experience business thinks "my experience, discounted" rather than "a
// percentage discount that happens to be on an experience".
//
// Mirrors ExperienceMechanic in backend/models/enums.py.
export const EXPERIENCE_MECHANICS = [
  { value: "PERCENT", label: "% Discount", hint: "e.g. 20% off the experience" },
  { value: "DOLLAR", label: "$ Saving", hint: "e.g. $30 off the experience" },
  { value: "BOGO", label: "Buy One Get One Free", hint: "No value needed" },
];

// spec 9.4: Version One optimises a numeric value, so only the two templates that have
// one are eligible. spec 9.2 additionally excludes Custom Offers.
export const SMART_ELIGIBLE_TEMPLATES = ["PERCENTAGE_DISCOUNT", "DOLLAR_DISCOUNT"];

/** A blank payload for a template, matching the backend's required fields. */
export function emptyPayload(template) {
  switch (template) {
    case "PERCENTAGE_DISCOUNT":
      return { template, discount_percent: 20, applies_to: "TOTAL_BILL", item_name: "", conditions: "" };
    case "DOLLAR_DISCOUNT":
      // Defaulted, like the percentage above: the selector has no empty state, so
      // starting blank would show a placeholder that is not one of the answers (G-10).
      return { template, dollar_amount: 10, applies_to: "TOTAL_BILL", item_name: "", conditions: "" };
    case "BOGO":
    case "FREE_ITEM":
      return { template, item_name: "", conditions: "" };
    case "FIXED_PRICE":
      return { template, item_name: "", price: "", conditions: "" };
    case "KIDS_EAT_FREE":
      return { template, conditions: "" };
    case "PACKAGE_DEAL":
      return { template, description: "", price: "", conditions: "" };
    case "EXPERIENCE":
      // No mechanic preselected (round 7, SP-24). It is a required choice, and
      // defaulting one would have the merchant save whichever we guessed.
      return { template, description: "", mechanic: "", discount_percent: "",
               dollar_amount: "", price: "", conditions: "" };
    case "CUSTOM":
      // Custom stays unpriced by design: free text, no numeric value, never Smart.
      return { template, description: "", conditions: "" };
    default:
      return { template };
  }
}

/**
 * Client-side validation mirroring the backend, so a merchant is not sent on a
 * round-trip to learn a rule the form could have shown. Returns an error string, or
 * null when valid.
 */
export function validatePayload(template, p) {
  // No currency check here any more (round 7, SP-25b, revised). It blocked the save, and
  // Robin's call is that refusing a finished offer over punctuation is friction for
  // nothing: "i think it can just auto-correct it instead of showing the warning".
  // fixCurrency does it on blur instead, and the backend normalises as a backstop.
  const needsItem = p.applies_to === "SPECIFIC_ITEM";
  if (needsItem && !String(p.item_name || "").trim()) {
    return "Name the item this offer applies to.";
  }
  switch (template) {
    case "PERCENTAGE_DISCOUNT":
      if (!DISCOUNT_STEPS.includes(Number(p.discount_percent))) {
        return "Choose a discount between 10% and 95%.";
      }
      return null;
    case "DOLLAR_DISCOUNT":
      // Mirrors the percentage check (G-10). The Select cannot produce anything off the
      // list, but a prefill from an AI suggestion or an older record can.
      if (!DOLLAR_STEPS.includes(Number(p.dollar_amount))) {
        return `Choose an amount between $${DOLLAR_STEPS[0]} and $${DOLLAR_STEPS[DOLLAR_STEPS.length - 1]}.`;
      }
      return null;
    case "FIXED_PRICE":
      if (!String(p.item_name || "").trim()) return "Name the item.";
      if (!(Number(p.price) > 0)) return "Enter a price greater than zero.";
      return null;
    case "BOGO":
    case "FREE_ITEM":
      if (!String(p.item_name || "").trim()) return "Name the item.";
      return null;
    case "EXPERIENCE": {
      if (!String(p.description || "").trim()) return "Describe the experience.";
      // The mechanic drives everything below it, so it is checked first (SP-24).
      if (!p.mechanic) return "Choose how this experience is redeemed.";
      if (p.mechanic === "PERCENT" && !DISCOUNT_STEPS.includes(Number(p.discount_percent))) {
        return "Choose a discount between 10% and 95%.";
      }
      if (p.mechanic === "DOLLAR" && !DOLLAR_STEPS.includes(Number(p.dollar_amount))) {
        return `Choose an amount between $${DOLLAR_STEPS[0]} and $${DOLLAR_STEPS[DOLLAR_STEPS.length - 1]}.`;
      }
      return null;
    }
    case "PACKAGE_DEAL":
    case "CUSTOM":
      if (!String(p.description || "").trim()) return "Describe the offer.";
      return null;
    default:
      return null;
  }
}

/** Strip empty optional strings so the API receives null rather than "". */
export function cleanPayload(p) {
  const out = { ...p };
  ["item_name", "conditions", "description"].forEach((k) => {
    if (k in out && !String(out[k] ?? "").trim()) delete out[k];
  });
  // discount_percent BELONGS in this list and was handled separately below, coerced
  // with Number() and never dropped (round 12, FP-19). Number("") is 0, not null, so a
  // blank percentage reached the API as a real zero. Harmless on the templates that
  // require one, fatal on Experience: choosing the $ Saving or Buy One Get One mechanic
  // clears the percentage to "", and ExperiencePayload refuses a dollar or BOGO offer
  // that "also carries a percentage". Only the % Discount mechanic could ever be saved.
  ["discount_percent", "dollar_amount", "price"].forEach((k) => {
    if (k in out) {
      if (String(out[k] ?? "").trim() === "") delete out[k];
      else out[k] = Number(out[k]);
    }
  });

  // applies_to is only meaningful on the two item-scoped templates that declare it.
  // item_name may only be dropped on THOSE templates, and only when the offer applies
  // to the whole bill. BOGO, Free Item and Fixed Price have no applies_to at all and
  // require item_name outright (models/offers.py), so stripping it there sent the API
  // a payload it must reject.
  const itemScoped = "applies_to" in emptyPayload(p.template);
  if (!itemScoped) {
    delete out.applies_to;
  } else if (out.applies_to !== "SPECIFIC_ITEM") {
    delete out.item_name;
  }
  return out;
}

function Conditions({ value, onChange }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground">Conditions (optional)</Label>
      {/* Free text a customer reads, so it gets the browser's spellchecker like the
          description does (SP-25). en-AU where the browser honours it, which is the
          difference between "favourite" passing and being underlined on every offer. */}
      {/* Round 12, FP-16, Joel's wording. The old example described a RESTRICTION;
          a spend threshold is the condition merchants actually reach for, and it
          shows the shape the field wants. */}
      <Input
        spellCheck="true"
        lang="en-AU"
        placeholder="e.g. With any spend over $10"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

/** Applies To + the item name it conditionally requires (spec 9.1). */
// No currency correction on this component's item field. fixOnBlur lives in
// OfferTemplateForm and is not in scope here, and threading it through for a short item
// name ("Flat White") is coupling for little gain - the backend normalises item_name
// regardless (round 7, SP-25b).
function AppliesTo({ payload, set }) {
  return (
    <>
      <div className="space-y-1.5">
        <Label className="text-xs text-muted-foreground">Applies to</Label>
        <RadioGroup
          value={payload.applies_to ?? "TOTAL_BILL"}
          onValueChange={(v) => set("applies_to", v)}
          className="flex gap-4 pt-1"
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem value="TOTAL_BILL" id="applies-total" />
            <Label htmlFor="applies-total" className="text-sm font-normal cursor-pointer">Total bill</Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem value="SPECIFIC_ITEM" id="applies-item" />
            <Label htmlFor="applies-item" className="text-sm font-normal cursor-pointer">A specific item</Label>
          </div>
        </RadioGroup>
      </div>
      {payload.applies_to === "SPECIFIC_ITEM" && (
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Item name</Label>
          <Input
            placeholder="e.g. Flat White"
            value={payload.item_name ?? ""}
            onChange={(e) => set("item_name", e.target.value)}
          />
        </div>
      )}
    </>
  );
}

export default function OfferTemplateForm({ template, payload, onChange }) {
  const set = (key, value) => onChange({ ...payload, [key]: value });
  // Round 11, SP-24. `set` spreads the payload PROP, so two calls in one handler do
  // NOT compose: the second rebuilds from the value the first started with and throws
  // the first away. Choosing an Experience mechanic did exactly that and the field
  // reset itself on every click. Any handler touching more than one key uses this.
  const setMany = (patch) => onChange({ ...payload, ...patch });

  // Round 7, SP-25b. Currency is CORRECTED rather than refused - Robin: "just
  // auto-correct it instead of showing the warning - easier and less friction".
  //
  // On BLUR, not on every keystroke: fixing it mid-word would fight the merchant while
  // they are still typing, rewriting "50$" the instant the symbol lands.
  //
  // The note is deliberate and is not a warning: it does not block and needs no
  // dismissing. Silently rewriting somebody's own words is the part worth avoiding - a
  // merchant who typed "50$ off" and later reads "$50 off" should not have to wonder
  // whether they typed it wrong or something changed it.
  const [corrected, setCorrected] = useState(null);
  const fixOnBlur = (key) => (e) => {
    const before = e.target.value;
    const after = fixCurrency(before);
    if (after === before) {
      // Clean now, so drop any note this field was still showing.
      if (corrected?.key === key) setCorrected(null);
      return;
    }
    set(key, after);
    // The note quotes the FIRST offending amount rather than the whole field, so it
    // reads as "changed 50$ to $50" instead of echoing a paragraph back at them.
    const bad = badCurrency(before);
    setCorrected({ key, before: bad, after: fixCurrency(bad) });
  };

  /** The note under a field, when this one was just corrected. */
  const CurrencyNote = ({ field }) =>
    corrected?.key === field ? (
      <p className="text-[11px] text-muted-foreground">
        Changed {corrected.before} to {corrected.after}, so the dollar sign reads first.
      </p>
    ) : null;

  switch (template) {
    case "PERCENTAGE_DISCOUNT":
      return (
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Discount</Label>
            <Select
              value={String(payload.discount_percent ?? 20)}
              onValueChange={(v) => set("discount_percent", Number(v))}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {DISCOUNT_STEPS.map((n) => (
                  <SelectItem key={n} value={String(n)}>{n}% off</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <AppliesTo payload={payload} set={set} />
          <Conditions value={payload.conditions} onChange={(v) => set("conditions", v)} />
        </div>
      );

    case "DOLLAR_DISCOUNT":
      return (
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Amount off ($)</Label>
            {/* A Select, matching the percentage control (round 4, G-10). It was a
                number input stepping in cents, so a merchant could type $10.37 and the
                Smart bounds - which validate against DOLLAR_STEPS - would then refuse
                to save it. */}
            <Select
              value={payload.dollar_amount != null && payload.dollar_amount !== "" ? String(payload.dollar_amount) : ""}
              onValueChange={(v) => set("dollar_amount", Number(v))}
            >
              <SelectTrigger data-testid="offer-dollar-amount">
                <SelectValue placeholder="Choose an amount" />
              </SelectTrigger>
              <SelectContent>
                {DOLLAR_STEPS.map((n) => (
                  <SelectItem key={n} value={String(n)}>${n} off</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <AppliesTo payload={payload} set={set} />
          <Conditions value={payload.conditions} onChange={(v) => set("conditions", v)} />
        </div>
      );

    case "BOGO":
    case "FREE_ITEM":
      return (
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Item</Label>
            <Input
              placeholder={template === "BOGO" ? "e.g. Margherita Pizza" : "e.g. Garlic Bread"}
              value={payload.item_name ?? ""}
              onChange={(e) => set("item_name", e.target.value)}
              onBlur={fixOnBlur("item_name")}
            />
          </div>
          <Conditions value={payload.conditions} onChange={(v) => set("conditions", v)} />
        </div>
      );

    case "FIXED_PRICE":
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Item</Label>
              <Input
                placeholder="e.g. Lunch Special"
                value={payload.item_name ?? ""}
                onChange={(e) => set("item_name", e.target.value)}
                onBlur={fixOnBlur("item_name")}
              />
              <CurrencyNote field="item_name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Price ($)</Label>
              <Input
                type="number" min="0.01" step="0.01" placeholder="15.00"
                value={payload.price ?? ""}
                onChange={(e) => set("price", e.target.value)}
              />
            </div>
          </div>
          <Conditions value={payload.conditions} onChange={(v) => set("conditions", v)} />
        </div>
      );

    case "KIDS_EAT_FREE":
      return (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Children eat free. Add any conditions below, such as an age limit or the
            requirement to buy an adult meal.
          </p>
          <Conditions value={payload.conditions} onChange={(v) => set("conditions", v)} />
        </div>
      );

    case "PACKAGE_DEAL":
      return (
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">What&apos;s included</Label>
            <Textarea
              spellCheck="true" lang="en-AU"
              rows={3} placeholder="e.g. Two mains, a side and a bottle of house wine"
              value={payload.description ?? ""}
              onChange={(e) => set("description", e.target.value)}
              onBlur={fixOnBlur("description")}
            />
            <CurrencyNote field="description" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Package price ($, optional)</Label>
            <Input
              type="number" min="0.01" step="0.01" placeholder="65.00"
              value={payload.price ?? ""}
              onChange={(e) => set("price", e.target.value)}
              data-testid="offer-price"
            />
            <p className="text-[11px] text-muted-foreground">
              A price puts a figure on the card and lets this offer appear in your
              reporting. Without one it shows as a description only.
            </p>
          </div>
          <Conditions value={payload.conditions} onChange={(v) => set("conditions", v)} />
        </div>
      );

    // Its own branch now (round 7, SP-24). It shared Package Deal's since round 4,
    // which was right while both were "a described thing at a price" - but a Package
    // Deal IS priced and an Experience is DISCOUNTED, and conflating the two is what
    // left "tree top adventure" rendering as nothing but its own name.
    case "EXPERIENCE":
      return (
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Describe the experience</Label>
            <Textarea
              spellCheck="true" lang="en-AU"
              rows={2}
              placeholder="e.g. Tree top adventure for two"
              value={payload.description ?? ""}
              onChange={(e) => set("description", e.target.value)}
              onBlur={fixOnBlur("description")}
            />
            <CurrencyNote field="description" />
          </div>

          {/* The mechanic FIRST, because the value below depends on it. Round 3 already
              settled this ordering for the offer modal: a field whose meaning depends on
              another must come after it, or the merchant is guessing. */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">
              How is it redeemed? <span className="text-red-500">*</span>
            </Label>
            <Select
              value={payload.mechanic || ""}
              onValueChange={(v) => {
                // ONE call. Three separate sets discarded the mechanic (round 11).
                // The values are cleared because the backend REFUSES a payload
                // carrying both, a merchant who set buy-one-get-one and 20% having
                // described two different offers.
                setMany({ mechanic: v, discount_percent: "", dollar_amount: "" });
              }}
            >
              <SelectTrigger data-testid="experience-mechanic">
                <SelectValue placeholder="Choose one" />
              </SelectTrigger>
              <SelectContent>
                {EXPERIENCE_MECHANICS.map((m) => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {payload.mechanic && (
              <p className="text-[11px] text-muted-foreground">
                {EXPERIENCE_MECHANICS.find((m) => m.value === payload.mechanic)?.hint}
              </p>
            )}
          </div>

          {/* Same steps as the standalone discount templates, so an Experience discount
              is exactly as constrained. Without this a merchant could pick 17% here and
              nowhere else, and the backend would refuse it on save. */}
          {payload.mechanic === "PERCENT" && (
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Discount (%)</Label>
              <Select
                value={payload.discount_percent ? String(payload.discount_percent) : ""}
                onValueChange={(v) => set("discount_percent", Number(v))}
              >
                <SelectTrigger data-testid="experience-percent">
                  <SelectValue placeholder="Choose a discount" />
                </SelectTrigger>
                <SelectContent>
                  {DISCOUNT_STEPS.map((n) => (
                    <SelectItem key={n} value={String(n)}>{n}% off</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {payload.mechanic === "DOLLAR" && (
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Amount off ($)</Label>
              <Select
                value={payload.dollar_amount ? String(payload.dollar_amount) : ""}
                onValueChange={(v) => set("dollar_amount", Number(v))}
              >
                <SelectTrigger data-testid="experience-dollar">
                  <SelectValue placeholder="Choose an amount" />
                </SelectTrigger>
                <SelectContent>
                  {DOLLAR_STEPS.map((n) => (
                    <SelectItem key={n} value={String(n)}>${n} off</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">
              Normal price ($, optional)
            </Label>
            <Input
              type="number" min="0.01" step="0.01" placeholder="120.00"
              value={payload.price ?? ""}
              onChange={(e) => set("price", e.target.value)}
              data-testid="offer-price"
            />
            <p className="text-[11px] text-muted-foreground">
              What it usually costs. Context for the discount, not the offer itself.
            </p>
          </div>

          <Conditions value={payload.conditions} onChange={(v) => set("conditions", v)} />
        </div>
      );

    case "CUSTOM":
      return (
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Describe your offer</Label>
            <Textarea
              spellCheck="true" lang="en-AU"
              rows={4}
              placeholder="Describe exactly what the customer gets"
              value={payload.description ?? ""}
              onChange={(e) => set("description", e.target.value)}
              onBlur={fixOnBlur("description")}
            />
            <CurrencyNote field="description" />
            {/* Round 4, G-12. Confirmed rather than changed: whatever is typed here IS
                the offer, and the merchant has to honour it. Nothing about that is new,
                but a merchant should know it before they type rather than when a
                customer presents it. */}
            <p className="text-[11px] text-amber-600">
              Whatever you write here is the offer customers see and can redeem, word for
              word, so make sure you can honour it.
            </p>
          </div>
          <Conditions value={payload.conditions} onChange={(v) => set("conditions", v)} />
        </div>
      );

    default:
      return null;
  }
}
